/**
 * 0x API version header value.
 */
export const ZERO_X_API_VERSION = 'v2';

/**
 * Default parameters for 0x cross-chain quotes.
 *
 * `sortQuotesBy` is intentionally omitted here because it is dynamically
 * determined per request based on the sell amount USD value.
 *
 * No bridges are excluded at the API level; instead, server-side selection
 * deprioritises fallback providers (bungee_auto and Mayan variants except mayan_swift).
 */
export const ZERO_X_CROSS_CHAIN_DEFAULT_PARAMS = {
  maxQuotes: 10,
} as const;

/**
 * Solana chain identifier for 0x API.
 */
export const ZERO_X_SOLANA_CHAIN_ID = 'solana';

/**
 * Numeric chain ID for Solana in 0x API (alternative).
 */
export const ZERO_X_SOLANA_CHAIN_ID_NUMERIC = 999999999991;

/**
 * Internal Solana chain ID used by our app (from viem defineChain).
 * The 0x API does not recognise this value — use {@link normalizeChainIdFor0xApi} to convert.
 */
const INTERNAL_SOLANA_CHAIN_ID = 1399811149;

/**
 * Convert an internal chain ID to the value the 0x API expects.
 * Specifically, our internal Solana chain ID (1399811149) is mapped to `'solana'`.
 * All other chain IDs pass through unchanged.
 */
export const normalizeChainIdFor0xApi = (chainId: number | string): number | string => {
  if (chainId === INTERNAL_SOLANA_CHAIN_ID || chainId === String(INTERNAL_SOLANA_CHAIN_ID)) {
    return ZERO_X_SOLANA_CHAIN_ID;
  }
  return chainId;
};

/**
 * Convert a 0x API chain ID to our internal chain ID.
 * Specifically, the 0x API Solana chain ID (999999999991) is mapped to our
 * internal Solana chain ID (1399811149).
 * All other chain IDs pass through as numbers.
 */
export const normalizeChainIdFromZeroXApi = (chainId: number | string): number => {
  if (chainId === ZERO_X_SOLANA_CHAIN_ID) {
    return INTERNAL_SOLANA_CHAIN_ID;
  }
  const numericId = Number(chainId);
  if (numericId === ZERO_X_SOLANA_CHAIN_ID_NUMERIC) {
    return INTERNAL_SOLANA_CHAIN_ID;
  }
  return numericId;
};
